import React, { useState, useRef, useEffect } from 'react';
import classes from './VideoPlayer.module.css';
import PrimaryButton from '../Buttons/PrimaryButton/PrimaryButton';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { urls } from '../../Routes/Urls';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExpand } from '@fortawesome/free-solid-svg-icons';

const VideoPlayer = ({ videoSrc, videoId, button = false }) => {
  const history = useHistory();
  const [isPlaying, setIsPlaying] = useState(true);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      setShowPlayButton(true);
    } else {
      video.play();
      setIsPlaying(true);
      setShowPlayButton(false);
    }
  };

  const handleFullScreen = () => {
    const video = videoRef.current;
    
    // Safari specific video fullscreen
    if (typeof video.webkitEnterFullscreen !== 'undefined') {
      video.webkitEnterFullscreen();
    } 
    // Standard fullscreen for other browsers
    else if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.mozRequestFullScreen) {
      video.mozRequestFullScreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  };

  useEffect(() => {
    const video = videoRef.current;

    const handlePlay = () => {
      setIsPlaying(true);
      setShowPlayButton(false);
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowPlayButton(true);
    };

    // Set up event listeners for video if it exists
    if (video) {
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
    }

    return () => {
      // Clean up event listeners
      if (video) {
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
      }
    };
  }, []);

  // Set up intersection observer to load video when in view
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !videoLoaded) {  
            // Set the actual video source when in view
            if (videoRef.current) {
              videoRef.current.src = videoSrc;
              videoRef.current.load();
              setVideoLoaded(true);
            }
            
            // Stop observing once video is loaded
            observer.unobserve(containerRef.current);
          }
        });
      },
      { 
        threshold: 0.7  // Load when 80% of the component is visible
      }
    );

    // Start observing
    observer.observe(containerRef.current);

    // Clean up
    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [videoSrc, videoLoaded]);

  return (
    <div ref={containerRef} className={classes.mainContainer}>
      <div className={classes.videoFrame}>
      {!videoLoaded && (
  <div className={classes.placeholder}>
    <svg className={classes.loadingIcon} viewBox="0 0 24 24">
      <circle className={classes.loadingCircle} cx="12" cy="12" r="10" />
    </svg>
  </div>
)}
        <video
          ref={videoRef}
          id={videoId || "video"}
          onClick={handlePlayPause}
          autoPlay={videoLoaded}
          muted
          loop
          playsInline
          style={{ display: videoLoaded ? 'block' : 'none' }}
        />
        {showPlayButton && videoLoaded && (
          <div className={classes.controlsContainer}>
            <div className={classes.playButton} onClick={handlePlayPause}>
              <div className={classes.playIcon} />
            </div>
            <FontAwesomeIcon
              icon={faExpand}
              className={classes.fullscreenIcon}
              onClick={handleFullScreen}
            />
          </div>
        )}
      </div>
      {button && (
        <PrimaryButton
          className={classes.btn}
          onClick={() => history.push(urls.LISTING)}
        >
          Explore Our Collection
        </PrimaryButton>
      )}
    </div>
  );
};

export default VideoPlayer;